import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import type { Database } from "@/integrations/supabase/types";

async function getPremiumEmailFromRequest(): Promise<string | null> {
  try {
    const request = getRequest();
    const authHeader = request?.headers?.get("authorization");
    if (!authHeader?.startsWith("Bearer ")) return null;
    const token = authHeader.slice("Bearer ".length).trim();
    if (!token) return null;

    const SUPABASE_URL = process.env.SUPABASE_URL;
    const SUPABASE_PUBLISHABLE_KEY = process.env.SUPABASE_PUBLISHABLE_KEY;
    if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) return null;

    const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    });
    const { data, error } = await supabase.auth.getUser(token);
    if (error || !data?.user?.email) return null;
    const email = data.user.email.toLowerCase();

    const { data: premium } = await supabaseAdmin
      .from("premium_access")
      .select("active_until")
      .eq("email", email.toLowerCase())
      .gte("active_until", new Date().toISOString())
      .limit(1)
      .maybeSingle();
    return premium ? email : null;
  } catch {
    return null;
  }
}


async function assertAdmin(userId: string) {
  const { data, error } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Endast administratörer.");
}

const QuestionInput = z.object({
  id: z.string().uuid().optional(),
  category: z.string().min(1).max(80),
  question: z.string().min(3).max(2000),
  options: z.array(z.string().min(1).max(500)).min(2).max(6),
  correct: z.number().int().min(0).max(5),
  explanation: z.string().max(4000).default(""),
  sort_order: z.number().int().optional(),
  active: z.boolean().optional(),
});

export const listAllQuestions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.userId);
    const { data, error } = await supabaseAdmin
      .from("questions")
      .select("id, category, question, options, correct, explanation, active, sort_order, updated_at")
      .order("category", { ascending: true })
      .order("sort_order", { ascending: true });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const upsertQuestion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => QuestionInput.parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    if (data.correct >= data.options.length) {
      throw new Error("Rätt svar måste motsvara ett alternativ.");
    }
    const payload: any = {
      category: data.category,
      question: data.question,
      options: data.options,
      correct: data.correct,
      explanation: data.explanation ?? "",
      active: data.active ?? true,
      sort_order: data.sort_order ?? 0,
      updated_at: new Date().toISOString(),
    };
    if (data.id) payload.id = data.id;
    const { data: row, error } = await supabaseAdmin
      .from("questions")
      .upsert(payload)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return row;
  });

export const deleteQuestion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const { error } = await supabaseAdmin.from("questions").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const listPublicQuestions = createServerFn({ method: "GET" })
  .handler(async () => {
    const { data, error } = await supabaseAdmin
      .from("questions_public")
      .select("id, category, question, options, free_pool, translations")
      .order("sort_order", { ascending: true });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

// Public grading endpoint: validates an answer server-side without
// exposing the answer key to the client. Returns the correct index and
// localized explanation only after the user submits an attempt.
export const gradeQuestion = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z.object({
      questionId: z.string().uuid(),
      selected: z.number().int().min(0).max(5),
      lang: z.string().min(2).max(8).optional(),
    }).parse(input)
  )
  .handler(async ({ data }) => {
    const { data: row, error } = await supabaseAdmin
      .from("questions")
      .select("correct, explanation, translations, active, free_pool, source_label, source_url")
      .eq("id", data.questionId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row || !row.active) throw new Error("Question not found");

    // Premium content gate: non-free-pool questions require an authenticated
    // user with active premium access. This prevents anonymous callers from
    // scraping correct answers / explanations for paid questions.
    const premiumEmail = await getPremiumEmailFromRequest();
    if (!row.free_pool && !premiumEmail) {
      throw new Error("Premium required");
    }
    const isPremium = !!premiumEmail;

    const lang = (data.lang ?? "sv") as string;
    const t = (row.translations as any)?.[lang];
    const explanation: string =
      (t && typeof t.explanation === "string" && t.explanation) ||
      (row.explanation ?? "");
    return {
      correctIndex: row.correct as number,
      correct: data.selected === row.correct,
      explanation,
      sourceLabel: (row.source_label as string | null) ?? null,
      // Källänken är en Premium-förmån – exkluderas för gratisanvändare.
      sourceUrl: isPremium ? ((row.source_url as string | null) ?? null) : null,
      isPremium,
    };
  });


/**
 * Publika, fria övningsfrågor för ett ämnesområde (SEO-sidorna /provfragor/*).
 * Returnerar aldrig rätt svar eller förklaring – endast frågetext och alternativ.
 */
export const listTopicQuestions = createServerFn({ method: "GET" })
  .inputValidator((input) =>
    z.object({ category: z.string().min(1).max(80), limit: z.number().int().min(1).max(20).optional() }).parse(input)
  )
  .handler(async ({ data }) => {
    const { data: rows, error } = await supabaseAdmin
      .from("questions_public")
      .select("id, category, question, options, free_pool")
      .eq("category", data.category)
      .order("sort_order", { ascending: true })
      .limit(data.limit ?? 8);
    if (error) throw new Error(error.message);
    return (rows ?? []).map((r) => ({
      id: r.id as string,
      question: r.question as string,
      options: Array.isArray(r.options) ? (r.options as string[]) : [],
    }));
  });
